import { useInstitutionsStore } from '@/stores/institutions';
import { useUsersStore } from '@/stores/users';
import {
  EKioskRouterNames,
  ERouterNames,
  EUserRoles,
  TNullable,
} from '@/types';
import {
  REQUIRED_FIELDS_BASIC,
  REQUIRED_FIELDS_DEVICE,
  REQUIRED_FIELDS_PATIENT,
  REQUIRED_FIELDS_PATIENT_DOSESPOT,
  REQUIRED_FIELDS_PATIENT_UNDER_19,
  REQUIRED_FIELDS_PROVIDER,
  REQUIRED_FIELDS_PROVIDER_DOSESPOT,
} from './constants';
import dayjs from 'dayjs';
import { PATIENT_MIN_AGE_FOR_HEIGHT_WEIGHT_REQUIRED } from '@/utils/constants';
import { areFieldsFilled } from './utils';
import { checkIsKiosk } from '@/utils/kiosk';

/**
 * Determines whether the current user must be redirected to an onboarding step
 * and returns the appropriate route if required.
 *
 * This hook evaluates onboarding completion based on the user's role,
 * institution configuration, and profile data. It supports multiple roles
 * (PATIENT, PROVIDER, DEVICE, ADMIN, COORDINATOR) and applies different
 * validation rules for each.
 *
 * The logic runs in the following order:
 * 1. If no user data is available, no redirect is required.
 * 2. If the user has not accepted the EULA, they are redirected to the
 *    first login (EULA) screen.
 * 3. Role-specific profile completeness checks are performed:
 *    - PATIENT: Validates required patient fields, including optional
 *      DoseSpot fields and age-based requirements (e.g. under 19).
 *    - PROVIDER: Validates required provider fields, including DoseSpot fields.
 *    - DEVICE: Ensures required fields are filled and a valid deviceId exists.
 *    - ADMIN / COORDINATOR: Validates basic fields and at least one spoken language.
 * 4. If any required fields are missing for the user’s role, they are redirected
 *    to the participant onboarding form.
 * 5. If all checks pass, no redirect is required.
 * The returned route differs depending on whether the app is running in kiosk mode.
 * @returns Redirect path to the required onboarding step, or null if onboarding is
 * complete and no redirect is needed.
 */
export const useOnboarding = () => {
  const { userData, checkRole, getUserInstitutionRoleByRole } = useUsersStore(
    (state) => ({
      userData: state.userData,
      checkRole: state.checkRole,
      getUserInstitutionRoleByRole: state.getUserInstitutionRoleByRole,
    }),
  );

  const requiredPatientFields = useInstitutionsStore((state) =>
    state.getRequiredPatientFields(),
  );

  const requiredProviderFields = useInstitutionsStore((state) =>
    state.getRequiredProviderFields(),
  );

  const dosespotEnabled = useInstitutionsStore((state) =>
    state.institutions.some(
      (institution) => institution.configuration?.dosespot?.enabled,
    ),
  );

  const isKiosk = checkIsKiosk();

  const getRedirect = (): TNullable<string> => {
    if (!userData) return null;
    if (!userData.meta?.eulaAcceptedAt) {
      return isKiosk
        ? `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FIRST_LOGIN}`
        : ERouterNames.FIRST_LOGIN;
    }
    // PATIENT CHECKS
    const basePatientFields = [...REQUIRED_FIELDS_PATIENT];
    const age = userData.dob ? dayjs().diff(dayjs(userData.dob), 'year') : null;
    const under19Fields =
      age !== null && age < PATIENT_MIN_AGE_FOR_HEIGHT_WEIGHT_REQUIRED
        ? REQUIRED_FIELDS_PATIENT_UNDER_19
        : [];

    const doseSpotPatientFields = dosespotEnabled
      ? [...REQUIRED_FIELDS_PATIENT_DOSESPOT, ...under19Fields]
      : [];

    const patientRequiredFields = [
      ...basePatientFields,
      ...doseSpotPatientFields,
      ...requiredPatientFields,
    ];

    const isPatientProfileComplete = areFieldsFilled(
      patientRequiredFields,
      userData,
    );

    // PROVIDER CHECKS
    const baseProviderFields = [...REQUIRED_FIELDS_PROVIDER];

    const doseSpotProviderFields = dosespotEnabled
      ? [...REQUIRED_FIELDS_PROVIDER_DOSESPOT]
      : [];

    const providerRequiredFields = [
      ...baseProviderFields,
      ...doseSpotProviderFields,
      ...requiredProviderFields,
    ];

    const isProviderProfileComplete = areFieldsFilled(
      providerRequiredFields,
      userData,
    );

    // COORDINATOR AND ADMIN CHECKS
    const isBasicFieldsComplete = areFieldsFilled(
      REQUIRED_FIELDS_BASIC,
      userData,
    );

    const isLanguageSet =
      Array.isArray(userData?.languagesSpoken) &&
      userData?.languagesSpoken.length >= 1;

    const isAdminAndCoordinatorProfileComplete =
      isBasicFieldsComplete && isLanguageSet;

    // DEVICE CHECKS
    const deviceId =
      getUserInstitutionRoleByRole(EUserRoles.DEVICE)?.meta?.deviceId ?? '';
    const isDeviceProfileComplete =
      areFieldsFilled(REQUIRED_FIELDS_DEVICE, userData) && deviceId !== '';

    const isRedirectToFirstLogin =
      (checkRole(EUserRoles.PATIENT) && !isPatientProfileComplete) ||
      (checkRole(EUserRoles.PROVIDER) && !isProviderProfileComplete) ||
      (checkRole(EUserRoles.DEVICE) && !isDeviceProfileComplete) ||
      ((checkRole(EUserRoles.ADMIN) || checkRole(EUserRoles.COORDINATOR)) &&
        !isAdminAndCoordinatorProfileComplete);
    if (isRedirectToFirstLogin) {
      const redirectPath = ERouterNames.FIRST_LOGIN__PARTICIPANT_FORM;
      return isKiosk
        ? `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__FIRST_LOGIN}/${redirectPath}`
        : `${ERouterNames.FIRST_LOGIN}/${redirectPath}`;
    }
    return null;
  };
  return getRedirect();
};
